// The notification list as a tiny external store (ADR 0010). One <Notifier />
// renders it; code anywhere calls notify(). Timing lives in the host, so this
// module stays synchronous and easy to test.

export type NotifyTone = 'success' | 'error' | 'info' | 'warning'

export interface NotifyOptions {
  tone: NotifyTone
  message: string
  action?: { label: string; onClick(): void }
}

export interface Notification extends NotifyOptions {
  id: number
  /** Bumped when a duplicate collapses into this one; restarts its timer. */
  revision: number
  /** When the latest copy arrived (ms since epoch). */
  lastAt: number
}

/** Visible at once; the oldest drops when a fifth arrives. */
export const MAX_VISIBLE = 4
/** Identical tone + message within this window collapse into one. */
export const DEDUPE_MS = 1000
/** Non-errors dismiss themselves after this long (paused on hover/focus). */
export const AUTO_DISMISS_MS = 5000

let items: readonly Notification[] = []
let sequence = 0
const listeners = new Set<() => void>()

function set(next: readonly Notification[]) {
  items = next
  for (const listener of listeners) listener()
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Oldest first. Stable between changes. */
export function getNotifications(): readonly Notification[] {
  return items
}

/** Show a notification. Returns its id (the existing one when collapsed). */
export function notify(options: NotifyOptions): number {
  const now = Date.now()
  const twin = items.find(
    (n) => n.tone === options.tone && n.message === options.message && now - n.lastAt < DEDUPE_MS
  )
  if (twin) {
    set(
      items.map((n) =>
        n === twin
          ? {
              ...n,
              ...(options.action && { action: options.action }),
              revision: n.revision + 1,
              lastAt: now
            }
          : n
      )
    )
    return twin.id
  }
  sequence += 1
  const next: Notification = { ...options, id: sequence, revision: 0, lastAt: now }
  set([...items, next].slice(-MAX_VISIBLE))
  return next.id
}

export function dismiss(id: number): void {
  if (items.some((n) => n.id === id)) set(items.filter((n) => n.id !== id))
}

/** Drop every notification. For tests (afterEach) and sign-out. */
export function resetNotifications(): void {
  if (items.length > 0) set([])
}
